import { isPlatformBrowser } from '@angular/common';
import { Directive, EventEmitter, HostBinding, HostListener, Inject, Input, Output, PLATFORM_ID } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Constants } from '../constants';
import { AlertService } from '../services';
export interface FileHandle {
  file: File,
  url: SafeUrl
}
@Directive({
  selector: '[appUploadFile]',
  standalone: true
})
export class UploadFileDirective {

  @Output() files: EventEmitter<FileHandle[]> = new EventEmitter();
  @Input() disableDragBackground: boolean = false; // Control background color on drag

  /**
   * Opt in to multi-file drops.
   *
   * Left `false` — the default — this directive behaves byte for byte as it
   * always has: more than one dropped file is refused outright with
   * `DRAG_SINGLE_RESTRICTION`, and the single file is size-checked here. Every
   * existing drop target in the app (quote/order PDFs, sample images, …) is a
   * single-file target and must keep exactly that behaviour, so the new path is
   * reachable only by a caller that asks for it.
   *
   * Set `true` and the whole `FileList` is passed through, emitted ONCE, with no
   * validation performed here — size, type and count are the caller's
   * `validateAttachments()` profile to enforce, so there is one rule set rather
   * than two that can disagree. (The old local `40000000` check disagreed with
   * every caller's own limit already.)
   */
  @Input() multiple: boolean = false;

  @HostBinding("style.background") background = "#fff";
  constructor(private sanitizer: DomSanitizer, public alertService: AlertService,
    @Inject(PLATFORM_ID) private platformId: any) { }
  @HostListener("dragover", ["$event"]) public onDragOver(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    if(this.disableDragBackground) {
      this.background = "#eee";
    }else {
      this.background = "#999";
    }
  }

  @HostListener("dragleave", ["$event"]) public onDragLeave(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    if(this.disableDragBackground) {
      this.background = "#fff";
    }else {
      this.background = "#eee";
    }
  }

  @HostListener('drop', ['$event']) public onDrop(evt: DragEvent) {
    evt.preventDefault();
    evt.stopPropagation();
    if(this.disableDragBackground) {
      this.background = "#fff";
    }else {
      this.background = "#eee";
    }

    if (!evt.dataTransfer?.files) {
      return;
    }

    if (this.multiple) {
      this.emitAll(evt.dataTransfer.files);
      return;
    }

    let files: FileHandle[] = [];
    if (evt.dataTransfer?.files.length > 1) {
      this.alertService.error(Constants.DRAG_SINGLE_RESTRICTION)
    } else {
      for (let i = 0; i < evt.dataTransfer?.files.length; i++) {
        if (evt.dataTransfer.files[i]) {
          const file = evt.dataTransfer.files[i];
          if (file.size > 40000000) {
            this.alertService.error(Constants.FILE_UPLOAD_CONSTANTS.IMAGE_SIZE_VALIDATION)
          } else {
            if (isPlatformBrowser(this.platformId)) {
              const url = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file));
              files.push({ file, url });
            }
            this.files.emit(files)
          }
        }
      }
    }
  }

  /**
   * The `[multiple]` path: every dropped file, emitted in ONE event.
   *
   * The single-file branch above emits inside its loop — harmless when it can
   * only ever hold one file, but a per-file emit here would make a 3-file drop
   * arrive as three separate batches, and a host that replaces its array on each
   * event would keep only the last. Building the array first and emitting after
   * the loop is what makes "drop 3 files" one append of 3.
   *
   * No size or type check: those belong to the caller's validation profile.
   */
  private emitAll(fileList: FileList): void {
    const files: FileHandle[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (!file) continue;
      // `url` is only meaningful in a browser; on the server there is no
      // createObjectURL, so the handle carries an empty one rather than throwing.
      const url = isPlatformBrowser(this.platformId)
        ? this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(file))
        : ('' as any);
      files.push({ file, url });
    }
    if (files.length) {
      this.files.emit(files);
    }
  }
}
